import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';

import {autoUpdate, offset, useFloating} from '@floating-ui/react';
import {
  GenesisFloatingPortal,
  GenesisFloatingFocusManager,
} from '@spaced-out/ui-design-system/lib/components/FocusManager';
import {
  getDateRangeDataByOption,
  getOptionByDataRange,
  getTimeRangeOptions,
  formatDateText,
} from 'src/utils/analytics-genesis';
import {getTodayInTimezone} from '@spaced-out/ui-design-system/lib/utils';
import {
  spaceNone,
  spaceXXSmall,
} from '@spaced-out/ui-design-system/lib/styles/variables/_space';
import {
  Menu,
  type MenuOption,
  type MenuProps,
} from '@spaced-out/ui-design-system/lib/components/Menu';
import {ClickAway} from '@spaced-out/ui-design-system/lib/utils/click-away';
import {mergeRefs} from '@spaced-out/ui-design-system/lib/utils/merge-refs';
import {
  Input,
  type InputProps,
} from '@spaced-out/ui-design-system/lib/components/Input';
import {DateRangePicker} from '@spaced-out/ui-design-system/lib/components/DateRangePicker';
import {
  Toast,
  toastApi,
  ToastTitle,
  TOAST_SEMANTIC,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import classify from '@spaced-out/ui-design-system/lib/utils/classify';

import css from './date-range-picker-dropdown.css';
import {getElevationValue} from '@spaced-out/ui-design-system';


type Placement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'left'
  | 'left-start'
  | 'left-end';

type DateRangePickerDropdownProps = InputProps & {
  onRangeSelect: (dateRangeData: DateRangeWithTimezone) => void;
  onMenuClose?: () => unknown;
  onMenuOpen?: () => unknown;
  enableThisYear?: boolean;
  menu?: MenuProps;
  minDate?: string;
  maxDate?: string;
  selectedDateRange?: DateRangeWithTimezone;
  placement?: Placement;
  isOnlyCustomRange?: boolean;
  displayFormat?: string;
  elevation?: string;
  classNames?: {
    inputBox?: string;
    dateRangePicker?: string;
  };
  removeOptions?: string[];
};

export const DateRangePickerDropdown = ({
  enableThisYear = false,
  size = 'medium',
  onRangeSelect,
  onMenuClose,
  onMenuOpen,
  minDate,
  maxDate,
  menu,
  selectedDateRange,
  placement = 'bottom-end',
  isOnlyCustomRange = false,
  displayFormat,
  classNames,
  elevation = 'modal',
  removeOptions,
  ...inputProps
}: DateRangePickerDropdownProps) => {
  const agencyTimezone = selectedDateRange?.timezone;
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const language = labelI18nInstance.language;

  const timeRangeOptions = React.useMemo(() => {
    const today = getTodayInTimezone(agencyTimezone ?? '');
    const currentYear = new Date(today).getFullYear();

    const timeRangeOptions = getTimeRangeOptions(currentYear, t);
    if (removeOptions && removeOptions.length > 0) {
      return timeRangeOptions.filter(
        (option) => !removeOptions.includes(option.key),
      );
    }
    return timeRangeOptions;
  }, [agencyTimezone, language]);

  const menuOptions = !enableThisYear
    ? timeRangeOptions.slice(1)
    : timeRangeOptions;

  // TODO: This component has to be completely controlled, state needs to be removed.
  const [selectedMenuOption, setSelectedMenuOption] =
    React.useState<MenuOption>(
      selectedDateRange
        ? getOptionByDataRange(selectedDateRange, agencyTimezone, menuOptions)
        : menuOptions[0],
    );

  React.useEffect(() => {
    setSelectedMenuOption(
      selectedDateRange
        ? getOptionByDataRange(selectedDateRange, agencyTimezone, menuOptions)
        : menuOptions[0],
    );
  }, [selectedDateRange, agencyTimezone, menuOptions, language]);

  const [isCustomRange, setIsCustomRange] = React.useState<boolean>(
    !isOnlyCustomRange ? selectedMenuOption.key === 'custom' : true,
  );
  const [dateRangeData, setDateRangeData] =
    React.useState<DateRangeWithTimezone>(
      selectedDateRange
        ? selectedDateRange
        : getDateRangeDataByOption(selectedMenuOption.key, agencyTimezone),
    );

  const inputValue = React.useMemo(() => {
    const {startDate, endDate} = dateRangeData;
    if (startDate && endDate) {
      if (selectedMenuOption.key !== 'custom') {
        return selectedMenuOption.label;
      } else {
        return `${formatDateText(
          startDate,
          displayFormat,
          language,
        )} - ${formatDateText(endDate, displayFormat, language)}`;
      }
    } else {
      return '--';
    }
  }, [selectedMenuOption, dateRangeData, language]);

  const {x, y, refs, strategy, context} = useFloating({
    open: true,
    strategy: 'absolute',
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(parseInt(spaceXXSmall))],
  });

  const handleError = () => {
    if (
      isCustomRange &&
      minDate &&
      dateRangeData.startDate &&
      minDate > dateRangeData.startDate
    ) {
      const updatedDateRange = {
        ...dateRangeData,
        startDate: minDate,
      } as const;
      setDateRangeData(updatedDateRange);
      onRangeSelect(updatedDateRange);
      onMenuOpen && onMenuOpen();
      const informationToast = (
        <Toast initialFocus={0} semantic={TOAST_SEMANTIC.information}>
          <ToastTitle>
            {`${t(
              'The_start_date_has_been_updated_from',
              'The start date has been updated from',
            )} ${formatDateText(
              dateRangeData.startDate ?? '',
              displayFormat,
              language,
            )} to ${formatDateText(minDate, displayFormat, language)} ${t(
              'to_meet_the_minimum_date_requirement',
              'to meet the minimum date requirement',
            )}.`}
          </ToastTitle>
        </Toast>
      );
      toastApi.show(informationToast, {
        autoClose: false,
      });
    }
  };

  const onMenuToggle = (isOpen: boolean) => {
    isOpen ? onMenuOpen && onMenuOpen() : onMenuClose && onMenuClose();
    setIsCustomRange(isOnlyCustomRange || false);
  };

  const handleMenuSelect = (option: MenuOption) => {
    if (option.key !== 'custom') {
      setSelectedMenuOption(option);
      const dateRangeData = getDateRangeDataByOption(
        option.key,
        agencyTimezone,
      );
      onRangeSelect(dateRangeData);
      setDateRangeData(dateRangeData);
    } else {
      setIsCustomRange(true);
    }
  };

  return (
    <ClickAway onChange={onMenuToggle} containsNestedFloatingPortals>
      {({isOpen = true, onOpen, triggerRef, boundaryRef, clickAway}) => (
        <div data-testid="Dropdown">
          <Input
            {...inputProps}
            readOnly
            size={size}
            boxRef={mergeRefs([refs.setReference, triggerRef])}
            classNames={{
              box: classify(css.inputBox, {
                [classNames?.inputBox ?? '']: !!classNames?.inputBox,
              }),
            }}
            value={inputValue}
            iconRightName={isOpen ? 'angle-up' : 'angle-down'}
            onContainerClick={(event: React.SyntheticEvent<HTMLElement>) => {
              event.stopPropagation();
              handleError();
              onOpen();
            }}
          />
          {isOpen && (
            <GenesisFloatingPortal>
              <GenesisFloatingFocusManager context={context}>
                <div
                  className={css.pickerWrapper}
                  ref={mergeRefs([refs.setFloating, boundaryRef])}
                  style={{
                    position: strategy,
                    top: y ?? spaceNone,
                    left: x ?? spaceNone,
                    '--menu-elevation': getElevationValue(elevation),
                  }}
                >
                  {isCustomRange ? (
                    <DateRangePicker
                      t={t}
                      locale={language}
                      onApply={(dateRangeData: DateRangeWithTimezone) => {
                        onRangeSelect(dateRangeData);
                        setDateRangeData(dateRangeData);
                        setSelectedMenuOption(
                          getOptionByDataRange(
                            dateRangeData,
                            agencyTimezone,
                            menuOptions,
                          ),
                        );
                        clickAway();
                      }}
                      classNames={{
                        wrapper: classify({
                          [classNames?.dateRangePicker ?? '']:
                            !!classNames?.dateRangePicker,
                        }),
                      }}
                      onCancel={() => {
                        clickAway();
                      }}
                      selectedDateRange={dateRangeData}
                      minDate={minDate}
                      hideTimezone
                      maxDate={maxDate}
                      startDateLabel={t('Start_Date', 'Start Date')}
                      endDateLabel={t('End_Date', 'End Date')}
                    />
                  ) : (
                    <Menu
                      {...menu}
                      onSelect={(option: MenuOption) => {
                        handleMenuSelect(option);
                        if (option.key !== 'custom') {
                          clickAway();
                        }
                      }}
                      classNames={{wrapper: css.menuOptionsCard}}
                      options={menuOptions}
                      selectedOption={selectedMenuOption}
                      size={size}
                      onTabOut={clickAway}
                      isFluid={false}
                    />
                  )}
                </div>
              </GenesisFloatingFocusManager>
            </GenesisFloatingPortal>
          )}
        </div>
      )}
    </ClickAway>
  );
};
